import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import './MonitoredEvents.less';
import { CheckCircleFilled, EyeOutlined } from '@ant-design/icons';
import {
    ExtendedMonitoredTv,
    MonitoredEvent,
    MonitoredMeasure,
    NoteEvent,
    OpsInstruction,
    WeatherEvent,
} from 'app/model/SOL49';
import { NoteToolbar, OpsInstructionToolbar, WeatherEventToolbar } from './MonitoredEventToolbar';
import { ShowModalEvent } from 'app/common/eventbus/Events';
import TimeUtils from 'app/common/util/TimeUtils';
import { TvRefLocation } from 'app/model/CORE';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import Utils from 'app/common/util/Utils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import {
    DEFAULT_MONITORED_EVENTS_SETTINGS,
    MonitoredEventsSettings,
} from 'app/components/dashboard/global/events/MonitoredEventsSettings';
import memoizeOne from 'memoize-one';
import { MonitoredEventsFilters } from 'app/components/dashboard/global/events/MonitoredEventFilters';
import { MonitoredEventPanelWrapper } from 'app/components/panels/predict/MonitoredEventPanelWrapper';
import { Collapse, Divider } from 'antd';

const { Panel } = Collapse;

interface MonitoredEventsProps {
    monitoredEvents: MonitoredEvent[];
    selectedEvents: MonitoredEvent[];
    onSelectedEventsChange: (selection: MonitoredEvent[]) => void;
    refLocs: TvRefLocation[];
    measures: MonitoredMeasure[];
    tvs: ExtendedMonitoredTv[];
    fmps: Set<string>;
}

interface MonitoredEventsState {
    selectedType: MonitoredEventType;
    opsInstructionFilter: string;
    eventsTextFilter: string;
    settings: MonitoredEventsSettings;
    filters: MonitoredEventsFilters;
}

export class MonitoredEventsV2 extends PureComponent<MonitoredEventsProps, MonitoredEventsState> {
    constructor(props) {
        super(props);
        const defaultFilters = MonitoredEventUtils.getFiltersOptions(
            this.props.monitoredEvents?.filter(MonitoredEventUtils.isWeatherEvent),
        );
        this.state = {
            selectedType: MonitoredEventType.OPS_INSTRUCTION,
            opsInstructionFilter: '',
            eventsTextFilter: '',
            settings: DEFAULT_MONITORED_EVENTS_SETTINGS,
            filters: {
                types: defaultFilters,
                options: defaultFilters,
            },
        };
    }

    componentDidUpdate(prevProps: Readonly<MonitoredEventsProps>) {
        if (Utils.isNotShallowEquals(prevProps.monitoredEvents, this.props.monitoredEvents)) {
            const filters = MonitoredEventUtils.getFiltersOptions(
                this.props.monitoredEvents?.filter(MonitoredEventUtils.isWeatherEvent),
            );
            this.setState({
                filters: {
                    types: filters,
                    options: filters,
                },
            });
        }
    }

    onFilter = (filters: MonitoredEventsFilters) => this.setState({ filters });
    onSettings = (settings: MonitoredEventsSettings) => this.setState({ settings });

    onOpenNote = (selected?: string) => {
        ShowModalEvent.fireNotClosable(
            <MonitoredEventPanelWrapper
                {...this.props}
                defaultType={MonitoredEventType.NOTE}
                selectedEvent={selected}
                settings={this.state.settings}
                onSettingsChange={this.onSettings}
                filters={this.state.filters}
                onFiltersChange={this.onFilter}
            />,
            MonitoredEventPanelWrapper.WIDTH,
        );
    };

    onOpenWeatherEvent = (selected?: string) => {
        ShowModalEvent.fireNotClosable(
            <MonitoredEventPanelWrapper
                {...this.props}
                defaultType={MonitoredEventType.WEATHER_EVENT}
                selectedEvent={selected}
                settings={this.state.settings}
                onSettingsChange={this.onSettings}
                filters={this.state.filters}
                onFiltersChange={this.onFilter}
            />,
            MonitoredEventPanelWrapper.WIDTH,
        );
    };

    onOpenOpsInstructions = (selected?: string) => {
        ShowModalEvent.fireNotClosable(
            <MonitoredEventPanelWrapper
                {...this.props}
                defaultType={MonitoredEventType.OPS_INSTRUCTION}
                selectedEvent={selected}
                settings={this.state.settings}
                onSettingsChange={this.onSettings}
                filters={this.state.filters}
                onFiltersChange={this.onFilter}
            />,
            MonitoredEventPanelWrapper.WIDTH,
        );
    };

    render() {
        const opsInstructions = this.props.monitoredEvents.filter(MonitoredEventUtils.isOpsInstruction);
        const notes = this.props.monitoredEvents.filter(MonitoredEventUtils.isNoteEvent);
        const weatherEvents = this.props.monitoredEvents.filter(MonitoredEventUtils.isWeatherEvent);

        return (
            <Collapse accordion={true} defaultActiveKey={this.state.selectedType}>
                <Panel
                    header={'OPS INSTRUCTIONS'}
                    key={MonitoredEventType.OPS_INSTRUCTION}
                    extra={Utils.size(opsInstructions)}>
                    {this.renderOpsInstructions(opsInstructions)}
                </Panel>
                <Panel header={'NOTES'} key={MonitoredEventType.NOTE} extra={Utils.size(notes)}>
                    {this.renderNotes(notes)}
                </Panel>
                <Panel
                    header={'EVENTS & WEATHER'}
                    key={MonitoredEventType.WEATHER_EVENT}
                    extra={Utils.size(weatherEvents)}>
                    {this.renderEventsAndWeather(weatherEvents)}
                </Panel>
            </Collapse>
        );
    }

    renderEventsAndWeather = (weatherEvents: WeatherEvent[]) => {
        let filteredEvents = Utils.isNotEmptyString(this.state.eventsTextFilter)
            ? weatherEvents.filter(ins => ins.title && ins.title.toUpperCase().includes(this.state.eventsTextFilter))
            : weatherEvents;
        filteredEvents = filteredEvents?.filter(event => this.state.filters.types.includes(event.type));
        return (
            <Flex direction="vertical">
                <WeatherEventToolbar
                    onAddEvent={this.onOpenWeatherEvent}
                    textFilter={this.state.eventsTextFilter}
                    onTextFilterChange={textFilter => this.setState({ eventsTextFilter: textFilter.toUpperCase() })}
                    filters={this.state.filters}
                    onFilter={this.onFilter}
                    settings={this.state.settings}
                    onSettings={this.onSettings}
                />
                <Headlines
                    monitoringEvents={this.filteredEvents(filteredEvents, this.state.settings)}
                    selected={this.props.selectedEvents}
                    selectable={true}
                    onSelectionChange={this.props.onSelectedEventsChange}
                    onOpen={event => this.onOpenWeatherEvent(event.id)}
                />
            </Flex>
        );
    };

    renderNotes = (notes: NoteEvent[]) => {
        return (
            <Flex direction="vertical">
                <NoteToolbar
                    onAddNote={this.onOpenNote}
                    onFilter={this.onFilter}
                    settings={this.state.settings}
                    onSettings={this.onSettings}
                />
                <Headlines
                    monitoringEvents={this.filteredEvents(notes, this.state.settings)}
                    selectable={true}
                    selected={this.props.selectedEvents}
                    onSelectionChange={this.props.onSelectedEventsChange}
                    onOpen={note => this.onOpenNote(note.id)}
                />
            </Flex>
        );
    };

    renderOpsInstructions = (opsInstructions: OpsInstruction[]) => {
        const filteredInstructions = Utils.isNotEmptyString(this.state.opsInstructionFilter)
            ? opsInstructions.filter(
                  ins =>
                      (ins.name && ins.name.toUpperCase().includes(this.state.opsInstructionFilter)) ||
                      (ins.title && ins.title.toUpperCase().includes(this.state.opsInstructionFilter)),
              )
            : opsInstructions;
        return (
            <Flex direction="vertical">
                <OpsInstructionToolbar
                    textFilter={this.state.opsInstructionFilter}
                    settings={this.state.settings}
                    onTextFilterChange={textFilter => this.setState({ opsInstructionFilter: textFilter.toUpperCase() })}
                    onFilter={this.onFilter}
                    onSettings={this.onSettings}
                />
                <Headlines
                    monitoringEvents={this.filteredEvents(filteredInstructions, this.state.settings)}
                    selected={this.props.selectedEvents}
                    onSelectionChange={this.props.onSelectedEventsChange}
                    onOpen={event => this.onOpenOpsInstructions(event.id)}
                />
            </Flex>
        );
    };

    filteredEvents = memoizeOne((events: MonitoredEvent[], settings: MonitoredEventsSettings) => {
        return settings.activeOnly ? events.filter(MonitoredEventUtils.isActive) : events;
    });
}

interface HeadlinesProps {
    monitoringEvents: MonitoredEvent[];
    selected: MonitoredEvent[];
    onSelectionChange: (selection: MonitoredEvent[]) => void;
    selectable?: boolean;
    onOpen: (event: MonitoredEvent) => void;
}

class Headlines extends PureComponent<HeadlinesProps> {
    private isSelected = (event: MonitoredEvent) => this.props.selected.some(e => e.id === event.id);

    private handleSelectionChange = (event: MonitoredEvent) => {
        let newSelection;
        if (this.isSelected(event)) {
            newSelection = this.props.selected.filter(e => e.id !== event.id);
        } else {
            newSelection = [...this.props.selected, event];
        }
        this.props.onSelectionChange(newSelection);
    };

    render() {
        const events = this.props.monitoringEvents.slice().sort(MonitoredEventUtils.cmpMonitoredEvent);
        return (
            <Flex direction="vertical" className="headlines overflow">
                {events.map((headline, idx) => {
                    const selected = this.isSelected(headline);
                    const className = `headline-news on-hover ${selected ? 'selected' : ''} ${this.props.selectable ? 'selectable' : ''}`;
                    let location;
                    if (MonitoredEventUtils.isNoteEvent(headline)) {
                        const fmps = headline.fmps ?? [];
                        const tvs = headline.fmps ?? [];
                        location = [...fmps, ...tvs].join(',');
                    } else if (MonitoredEventUtils.isWeatherEvent(headline)) {
                        location = headline.refLocs?.map(rl => rl.refId).join(',');
                    }
                    if (location) {
                        location = location.length < 10 ? location : location.slice(0, 10) + '...';
                    }
                    return (
                        <Flex
                            className={className}
                            direction={'vertical'}
                            key={idx}
                            onClick={() => (this.props.selectable ? this.handleSelectionChange(headline) : {})}>
                            <Flex
                                direction={'horizontal'}
                                style={{ gap: '0.5em', alignItems: 'center', width: '100%' }}>
                                <ConditionalRender condition={this.props.selectable}>
                                    <div className={'check-circle show-on-hover'} style={{ padding: '0 0.25em' }}>
                                        <CheckCircleFilled />
                                    </div>
                                </ConditionalRender>
                                <div>{TimeUtils.getDayMonthTime(headline.lastUpdate)}</div>
                                <ConditionalRender condition={MonitoredEventUtils.isOpsInstruction(headline)}>
                                    <div>- {(headline as OpsInstruction).name}</div>
                                </ConditionalRender>
                                <ConditionalRender condition={Utils.isNotEmptyString(location)}>
                                    <span>[{location}]</span>
                                </ConditionalRender>
                                <div style={{ paddingLeft: '0.5em', zIndex: 100, marginLeft: 'auto' }}>
                                    <EyeOutlined
                                        onClick={event => {
                                            event.stopPropagation();
                                            this.props.onOpen(headline);
                                        }}
                                    />
                                </div>
                            </Flex>
                            <Divider type={'horizontal'} style={{ width: '100%', borderColor: '#8C8B82' }} />
                            <Flex style={{ wordBreak: 'break-word', lineHeight: '1.2em' }}>{headline.title}</Flex>
                        </Flex>
                    );
                })}
            </Flex>
        );
    }
}
